import styled, { css, keyframes } from 'styled-components';

const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 transparent; }
  50% { box-shadow: 0 0 0 8px var(--sketch-glow); }
`;

export const SketchRoot = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 320px;
  overflow: hidden;
  border-radius: 16px;

  ${({ theme: { colors, isLightTheme } }) => css`
    background-color: ${isLightTheme ? colors.surfaceContainerHigh : colors.surfaceContainerLow};
    background-image: radial-gradient(${colors.outlineVariant} 1px, transparent 1px);
    background-size: 24px 24px;
  `}
`;

export const Drawing = styled.svg`
  position: absolute;
  inset: 0;
`;

export const Ink = styled.path`
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  opacity: 0.75;
  filter: drop-shadow(0 0 5px color-mix(in srgb, ${({ theme }) => theme.colors.primary} 45%, transparent));
  animation: ${draw} 2.4s ease-in-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    stroke-dashoffset: 0;
  }
`;

export const Soft = styled.path`
  fill: none;
  stroke: ${({ theme }) => theme.colors.outlineVariant};
  stroke-width: 1;
  opacity: 0.7;
`;

const FILL_OPACITY = 0.07;

const fillIn = keyframes`
  from { opacity: 0; }
  to { opacity: ${FILL_OPACITY}; }
`;

export const SoftFill = styled.rect`
  fill: ${({ theme }) => theme.colors.primary};
  opacity: 0;
  animation: ${fillIn} 1s ease-out 2s forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: ${FILL_OPACITY};
  }
`;

export const Label = styled.text<{ $strong?: boolean }>`
  font-family: inherit;
  font-size: ${({ $strong }) => ($strong ? '15px' : '12px')};
  font-weight: ${({ $strong }) => ($strong ? 600 : 500)};
  fill: ${({ theme, $strong }) => ($strong ? theme.colors.onSurface : theme.colors.onSurfaceVariant)};
  opacity: 0;
  animation: ${fadeIn} 0.8s ease-out 0.6s forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

export const SketchActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  gap: 8px;
  border-radius: 999px;
  --sketch-glow: color-mix(in srgb, ${({ theme }) => theme.colors.primary} 30%, transparent);
  animation: ${glow} 2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Guide = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  z-index: 1;
  left: ${({ $x }) => `${Math.round($x)}px`};
  top: ${({ $y }) => `${Math.round($y)}px`};
  transition:
    left 2.6s ease-in-out,
    top 3s ease-in-out;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
